import React, {useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import Row from "@meridian/components/row";
import {
    chainWalk, getLidarControllerManageByExceptionsRefreshInterval, getOrdinal
} from "../../util/Util";
import Column from "@amzn/meridian/column";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Icon from "@amzn/meridian/icon";
import Box from "@amzn/meridian/box";
import Tooltip from "@amzn/meridian/tooltip";
import Loader from "../../loader/Loader";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import {LidarOverviewContext} from "./context/LidarOverviewContext";
import {
    DEVICE_TYPE,
    DEVICE_TYPE_RENDER_STRING,
    EU_OV_LOCATION_LABEL_REGEX, EU_OV_LOCATION_LABEL_REGEX_WITH_OVD,
    NA_OV_LOCATION_LABEL_REGEX, NA_OV_LOCATION_LABEL_REGEX_WITH_OVD,
    LOCATION_MAPPING_ATTR,
} from "../../util/Constants";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";


const LidarManageByExceptions = ({intl}) => {

    const {viewModel, dispatch} = useContext(LidarOverviewContext);
    const [openAddModal, setOpenAddModal] = useState(false);
    const tableEndRef = useRef(null);
    const renderingOrder = [DEVICE_TYPE.LIDAR_CONTROLLER, DEVICE_TYPE.BRANCH_NODE, DEVICE_TYPE.LEAF_NODE,
        DEVICE_TYPE.LIDAR_SENSOR];

    useEffect(() => {
        if (!openAddModal && chainWalk(() => Object.keys(viewModel.lidarDeviceInformation).length, 0) > 0) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getLidarControllerManageByExceptionsRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, viewModel.lidarDeviceInformation]);

    const offlineDevicesList = Object.values(viewModel.deviceConnectivityList);
    //sort offline devices based on location label.
    offlineDevicesList.sort(function(a, b) {
        if (!a.locationLabel) {
            return 1;
        }
        if (!b.locationLabel) {
            return -1;
        }
        var locationLabelComparisonResult = a.locationLabel.localeCompare(b.locationLabel, undefined, {
            numeric: true,
            sensitivity: 'base'
        });
        if (locationLabelComparisonResult === 0 && a.deviceType === b.deviceType) {
            if (DEVICE_TYPE.LIDAR_SENSOR === a.deviceType) {
                return a.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LIDAR_SENSOR_INDEX].localeCompare(
                    b.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LIDAR_SENSOR_INDEX], undefined, {
                            numeric: true,
                            sensitivity: 'base'
                });
            } else if (DEVICE_TYPE.LEAF_NODE === a.deviceType) {
                return a.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LEAF_NODE_INDEX] &&
                    a.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LEAF_NODE_INDEX].localeCompare(
                    b.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LEAF_NODE_INDEX], undefined, {
                        numeric: true,
                        sensitivity: 'base'
                    });
            }
        }
        return locationLabelComparisonResult;
    });
    //Group offline devices based on device category
    const groups = offlineDevicesList && offlineDevicesList.reduce((groups, item) => {
            const group = (groups[item.deviceType] || []);
            group.push(item);
            groups[item.deviceType] = group;
            return groups;
        }, new Map());

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }
    const getData = () => {
        setOpenAddModal(false)
        dispatch({type: 'LOAD_MANAGE_BY_EXCEPTIONS_VIEW', data: {dispatcher: dispatch}});
    }

    //Based on device category, logic to compute location is different
    const renderDeviceLocation = (device) => {
        if (DEVICE_TYPE.LIDAR_SENSOR === device.deviceType
            && device.locationMappingAttributesMap && device.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LIDAR_SENSOR_INDEX]) {
                let deviceIndex = device.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LIDAR_SENSOR_INDEX];
                let ordinal = getOrdinal(deviceIndex);
                return device.locationLabel + " (" + deviceIndex + ordinal + ")"
        } else if (DEVICE_TYPE.LEAF_NODE === device.deviceType
                && (EU_OV_LOCATION_LABEL_REGEX.test(device.locationLabel)
                    || NA_OV_LOCATION_LABEL_REGEX.test(device.locationLabel)
                    || EU_OV_LOCATION_LABEL_REGEX_WITH_OVD.test(device.locationLabel)
                    || NA_OV_LOCATION_LABEL_REGEX_WITH_OVD.test(device.locationLabel))
                && device.locationMappingAttributesMap && device.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LEAF_NODE_INDEX]) {
                let deviceIndex = device.locationMappingAttributesMap[LOCATION_MAPPING_ATTR.LEAF_NODE_INDEX];
                let ordinal = getOrdinal(deviceIndex);
                return device.locationLabel + " (" + deviceIndex + ordinal + ")";
        } else if (DEVICE_TYPE.LIDAR_CONTROLLER === device.deviceType) {
            return device.locationLabel.at(0)+ " (" +device.locationLabel+ ")";
        }
        return device.locationLabel;
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Text type="b200">
                            <FormattedMessage id="lidar.health_refresh_note" defaultMessage="Note: Device health data is synchronized every 40 seconds." />
                        </Text>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom!'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    <Table
                        headerRows={1}
                        spacing="small"
                        showDividers={true}
                        showStripes={true}>
                        <TableRow highlightOnHover={false}>
                            <TableCell>
                                <FormattedMessage id="device.device_category" defaultMessage="Device Category"/>
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="device.offline_devices_locations" defaultMessage="Offline Devices Locations"/>
                            </TableCell>
                        </TableRow>
                        {renderingOrder.map((deviceType) => (
                            <TableRow key={deviceType}
                                      highlightOnHover={true}>
                                <TableCell>
                                    <b>{DEVICE_TYPE_RENDER_STRING[deviceType]}</b>
                                </TableCell>
                                <TableCell>
                                    {groups[deviceType] ?
                                        <Row spacing="small"
                                             wrap={"down"}>
                                        {groups[deviceType].map((device) => (
                                            <Tooltip position="top" title={device.identifier} key={device.identifier}>
                                                <Box type="outline" spacingInset="small">
                                                    {renderDeviceLocation(device)}
                                                </Box>
                                            </Tooltip>
                                        ))}
                                        </Row> : <FormattedMessage id="device.nodevice" defaultMessage="No Device"/>
                                    }
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                    <div ref={tableEndRef}/>
                    <FormattedMessage id="device.index_interpretation"
                                      defaultMessage="Note: Nth device(column/sensor) index denotes Nth device from conveyor side"/>
                    <Link href="https://w.amazon.com/bin/view/Last_Mile_Team/Smart-Station/Scanless/Lidar_Mechlite_Alpha_Launch_Plan/Maintenance_and_Troubleshooting">
                        <FormattedMessage id="device.troubleshootwiki" defaultMessage="Troubleshoot wiki"/>
                    </Link>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(LidarManageByExceptions)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/LidarManageByExceptions.js